/**
 * Voz de «Hablando con AROC»: dictar (micrófono → texto) y escuchar (bocina).
 *
 * El dictado lo transcribe el faster-whisper LOCAL de AROC (sin terceros, sin
 * gasto de modelo); la lectura usa la voz del propio navegador. Aquí viven las
 * partes puras, para probarlas sin MediaRecorder ni speechSynthesis.
 */

import { ApiError } from "@/api/client";

export const MAX_SEGUNDOS = 60;
export const BITS_POR_SEGUNDO = 32000;

/** El formato que este navegador sabe grabar (Chrome/Firefox: webm; Safari: mp4). */
export function mimeGrabacion(soporta: (tipo: string) => boolean): string | null {
  for (const t of ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"]) {
    if (soporta(t)) return t;
  }
  return null;
}

/** El texto de una respuesta como se dice en voz alta: sin tablas ni markdown. */
export function textoParaLeer(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, " ")
    .split("\n")
    .filter((l) => !/^\s*\|/.test(l)) // tablas
    .map((l) =>
      l
        .replace(/^\s{0,3}#{1,6}\s+/, "") // encabezados
        .replace(/^\s*(?:[-*+]|\d+[.)])\s+/, "") // viñetas y listas numeradas
        .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // enlaces → su texto
        .replace(/`([^`]*)`/g, "$1")
        .replace(/(\*\*|__|\*|_|~~)/g, ""),
    )
    .join("\n")
    .replace(/\n{2,}/g, ". ")
    .replace(/\s+/g, " ")
    .trim();
}

/** La voz en español de México si existe; si no, cualquier español. */
export function elegirVoz<T extends { lang: string }>(voces: T[]): T | null {
  const es = (v: T) => v.lang.toLowerCase().replace("_", "-");
  return (
    voces.find((v) => es(v) === "es-mx") ??
    voces.find((v) => es(v).startsWith("es-419") || es(v).startsWith("es-us")) ??
    voces.find((v) => es(v).startsWith("es")) ??
    null
  );
}

export interface Transcripcion {
  texto: string;
  segundos: number | null;
  ms: number;
}

export async function transcribir(audio: Blob, mime: string): Promise<Transcripcion> {
  const r = await fetch("/api/v1/analysis/voz", {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": mime.split(";")[0] },
    body: audio,
  });
  const cuerpo = await r.json().catch(() => null);
  if (!r.ok) {
    const e = (cuerpo?.error ?? cuerpo?.detail?.error) as { code?: string; message?: string } | undefined;
    throw new ApiError(r.status, e?.code ?? "http_error", e?.message ?? "No pude transcribir el audio.");
  }
  return cuerpo as Transcripcion;
}

const CLAVE_AUTO = "aroc.leerAuto";

export function leerAutoGuardado(): boolean {
  try {
    return globalThis.localStorage?.getItem(CLAVE_AUTO) === "1";
  } catch {
    return false;
  }
}

export function guardarLeerAuto(v: boolean): void {
  try {
    globalThis.localStorage?.setItem(CLAVE_AUTO, v ? "1" : "0");
  } catch {
    /* sin almacenamiento: sólo dura la pestaña */
  }
}

/** Lee en voz alta (cancela lo que estuviera leyendo). Sin soporte, no hace nada. */
export function leer(md: string): void {
  const s = globalThis.speechSynthesis;
  if (!s || typeof SpeechSynthesisUtterance === "undefined") return;
  s.cancel();
  const u = new SpeechSynthesisUtterance(textoParaLeer(md));
  const voz = elegirVoz(s.getVoices());
  if (voz) u.voice = voz;
  u.lang = voz?.lang ?? "es-MX";
  s.speak(u);
}

export function hayLectura(): boolean {
  return typeof globalThis.speechSynthesis !== "undefined";
}
